import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Skeleton,
  LinearProgress,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import assetService from "../../../services/assetService";

const getHealthColor = (score) => {
  if (score == null) return "#6B7280";
  if (score >= 80) return "#16A34A";
  if (score >= 60) return "#D97706";
  return "#DC2626";
};

const getHealthLabel = (score) => {
  if (score == null) return "Unknown";
  if (score >= 80) return "Good";
  if (score >= 60) return "Fair";
  return "Critical";
};

const getRulColor = (days) => {
  if (days == null) return "#6B7280";
  if (days > 90) return "#16A34A";
  if (days > 30) return "#D97706";
  return "#DC2626";
};

const getFailureColor = (prob) => {
  if (prob == null) return "#6B7280";
  const pct = prob > 1 ? prob : prob * 100;
  if (pct >= 70) return "#DC2626";
  if (pct >= 40) return "#D97706";
  return "#16A34A";
};

const formatFailure = (prob) => {
  if (prob == null) return "N/A";
  const pct = prob > 1 ? prob : prob * 100;
  return `${pct.toFixed(1)}%`;
};

const MetricRow = ({ label, value, color, barValue }) => (
  <Box sx={{ mb: 2.5 }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
      <Typography sx={{ fontSize: "13px", color: "#6B7280", fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "14px", fontWeight: 700, color }}>
        {value}
      </Typography>
    </Box>
    {barValue != null && (
      <LinearProgress
        variant="determinate"
        value={Math.min(barValue, 100)}
        sx={{
          height: 6,
          borderRadius: 3,
          bgcolor: "#F1F5F9",
          "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 3 },
        }}
      />
    )}
  </Box>
);

const DiagnosticsPopup = ({ open, onClose, assetId, assetName }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !assetId) return;
    setLoading(true);
    setData(null);
    setError(null);
    assetService
      .getAssetSummary(assetId)
      .then((res) => setData(res.data.data))
      .catch(() => setError("Failed to load diagnostics."))
      .finally(() => setLoading(false));
  }, [open, assetId]);

  const healthScore = data?.healthScore ?? null;
  const rulDisplay = data?.rul?.value ?? (data?.rulDays != null ? `${data.rulDays} days` : null);
  const rulDays = data?.rulDays ?? (rulDisplay ? parseInt(rulDisplay, 10) || null : null);
  const failureProbability = data?.failureProbability ?? null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: { borderRadius: "16px", overflow: "hidden" },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: "1px solid #F1F5F9",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: "11px", color: "#94A3B8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.25 }}>
            Diagnostics
          </Typography>
          <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#0F172A", lineHeight: 1.3 }}>
            {assetName ?? "Asset"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#94A3B8", "&:hover": { color: "#374151" } }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3, py: 3 }}>
        {loading ? (
          <>
            {[0, 1, 2].map((i) => (
              <Box key={i} sx={{ mb: 2.5 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
                  <Skeleton width={100} height={18} />
                  <Skeleton width={50} height={18} />
                </Box>
                <Skeleton variant="rounded" height={6} sx={{ borderRadius: 3 }} />
              </Box>
            ))}
          </>
        ) : error ? (
          <Typography sx={{ color: "#DC2626", fontSize: "14px", textAlign: "center", py: 2 }}>
            {error}
          </Typography>
        ) : (
          <>
            {/* Health Score */}
            <MetricRow
              label="Health Score"
              value={healthScore != null ? `${healthScore}/100 — ${getHealthLabel(healthScore)}` : "N/A"}
              color={getHealthColor(healthScore)}
              barValue={healthScore}
            />

            {/* RUL */}
            <MetricRow
              label="Remaining Useful Life (RUL)"
              value={rulDisplay ?? "N/A"}
              color={getRulColor(rulDays)}
              barValue={rulDays != null ? Math.min((rulDays / 365) * 100, 100) : null}
            />

            {/* Failure Probability */}
            <MetricRow
              label="Failure Probability"
              value={formatFailure(failureProbability)}
              color={getFailureColor(failureProbability)}
              barValue={
                failureProbability != null
                  ? failureProbability > 1
                    ? failureProbability
                    : failureProbability * 100
                  : null
              }
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DiagnosticsPopup;
